
import { useState } from 'react';

export default function ConsultationBooking() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    consultationType: '',
    preferredTime: '',
    message: '',
    experience: '',
    budget: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPayment, setShowPayment] = useState(false);

  const consultationFees = {
    'investment-consultation': 150,
    'property-development': 200,
    'business-planning': 175,
    'market-analysis': 125
  };

  const consultationNames = {
    'investment-consultation': 'Investment Strategy Consultation',
    'property-development': 'Property Development Consultation',
    'business-planning': 'Business Planning Session',
    'market-analysis': 'Market Analysis Review'
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.consultationType) {
      alert('Please select a consultation type');
      return;
    }

    setShowPayment(true);
  };

  const handlePayment = async () => {
    setIsSubmitting(true);
    
    try {
      const fee = consultationFees[formData.consultationType as keyof typeof consultationFees];
      
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: fee,
          currency: 'usd',
          description: consultationNames[formData.consultationType as keyof typeof consultationNames],
          metadata: {
            consultation_type: formData.consultationType,
            customer_name: formData.name,
            customer_email: formData.email,
            customer_phone: formData.phone,
            preferred_time: formData.preferredTime,
            experience: formData.experience,
            budget: formData.budget,
            message: formData.message
          }
        })
      });

      const { url } = await response.json();
      if (url) {
        window.location.href = url;
      }
    } catch (error) {
      console.error('Payment error:', error);
      alert('There was an error processing your payment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const selectedFee = formData.consultationType ? consultationFees[formData.consultationType as keyof typeof consultationFees] : 0;

  if (showPayment && formData.consultationType) {
    return (
      <section id="booking" className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">Consultation Booking Summary</h3>
            
            <div className="bg-emerald-50 rounded-lg p-6 mb-8">
              <h4 className="text-xl font-semibold text-emerald-800 mb-4">Consultation Details</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-emerald-700">Service:</span>
                    <span className="font-semibold text-emerald-800">
                      {consultationNames[formData.consultationType as keyof typeof consultationNames]}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-emerald-700">Duration:</span>
                    <span className="font-semibold text-emerald-800">
                      {formData.consultationType === 'property-development' ? '75 minutes' : 
                       formData.consultationType === 'market-analysis' ? '45 minutes' : '60 minutes'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-emerald-700">Client:</span>
                    <span className="font-semibold text-emerald-800">{formData.name}</span>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-emerald-700">Email:</span>
                    <span className="font-semibold text-emerald-800">{formData.email}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-emerald-700">Phone:</span>
                    <span className="font-semibold text-emerald-800">{formData.phone || 'Not provided'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-emerald-700">Preferred Time:</span>
                    <span className="font-semibold text-emerald-800">{formData.preferredTime || 'To be scheduled'}</span>
                  </div>
                </div>
              </div>
              <div className="border-t border-emerald-200 pt-4 mt-6">
                <div className="flex justify-between items-center">
                  <span className="text-emerald-700 text-lg">Consultation Fee:</span>
                  <span className="font-bold text-emerald-800 text-2xl">${selectedFee}</span>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <button
                onClick={handlePayment}
                disabled={isSubmitting}
                className="w-full bg-emerald-600 text-white py-4 px-6 rounded-lg font-semibold hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap text-lg"
              >
                {isSubmitting ? 'Processing Payment...' : `Pay & Book Consultation ($${selectedFee})`}
              </button>
              
              <button
                onClick={() => setShowPayment(false)}
                className="w-full bg-white text-gray-600 py-3 px-6 rounded-lg font-semibold border border-gray-300 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                Back to Form
              </button>
            </div>

            <p className="text-sm text-gray-600 text-center mt-6">
              After payment confirmation, we'll contact you within 24 hours to schedule your consultation at your preferred time.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="booking" className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Book Your Consultation
          </h2>
          <p className="text-xl text-gray-600">
            Schedule a personalized consultation with our glamping investment experts
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form id="consultation-booking">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                />
              </div>

              <div>
                <label htmlFor="consultationType" className="block text-sm font-medium text-gray-700 mb-2">
                  Consultation Type *
                </label>
                <select
                  id="consultationType"
                  name="consultationType"
                  value={formData.consultationType}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm pr-8"
                >
                  <option value="">Select a service</option>
                  <option value="investment-consultation">Investment Strategy Consultation - $150</option>
                  <option value="property-development">Property Development Consultation - $200</option>
                  <option value="business-planning">Business Planning Session - $175</option>
                  <option value="market-analysis">Market Analysis Review - $125</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="experience" className="block text-sm font-medium text-gray-700 mb-2">
                  Investment Experience
                </label>
                <select
                  id="experience"
                  name="experience"
                  value={formData.experience}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm pr-8"
                >
                  <option value="">Select experience level</option>
                  <option value="beginner">Beginner - New to investments</option>
                  <option value="intermediate">Intermediate - Some investment experience</option>
                  <option value="experienced">Experienced - Multiple investments</option>
                  <option value="expert">Expert - Professional investor</option>
                </select>
              </div>

              <div>
                <label htmlFor="budget" className="block text-sm font-medium text-gray-700 mb-2">
                  Investment Budget Range
                </label>
                <select
                  id="budget"
                  name="budget"
                  value={formData.budget}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm pr-8"
                >
                  <option value="">Select budget range</option>
                  <option value="under-50k">Under $50,000</option>
                  <option value="50k-100k">$50,000 - $100,000</option>
                  <option value="100k-250k">$100,000 - $250,000</option>
                  <option value="250k-500k">$250,000 - $500,000</option>
                  <option value="over-500k">Over $500,000</option>
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="preferredTime" className="block text-sm font-medium text-gray-700 mb-2">
                Preferred Time & Date
              </label>
              <input
                type="text"
                id="preferredTime"
                name="preferredTime"
                value={formData.preferredTime}
                onChange={handleChange}
                placeholder="e.g., Weekdays 2-4 PM EST, or specific date/time preference"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
              />
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                Additional Information
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={4}
                maxLength={500}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm resize-none"
                placeholder="Tell us about your specific goals, questions, or any additional information that would help us prepare for your consultation..."
              />
              <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
            </div>

            {formData.consultationType && (
              <div className="bg-emerald-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-emerald-800">
                      {consultationNames[formData.consultationType as keyof typeof consultationNames]}
                    </h4>
                    <p className="text-sm text-emerald-700">
                      Duration: {formData.consultationType === 'property-development' ? '75 minutes' : 
                                formData.consultationType === 'market-analysis' ? '45 minutes' : '60 minutes'}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-emerald-600">${selectedFee}</div>
                    <div className="text-sm text-emerald-700">Consultation Fee</div>
                  </div>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-emerald-600 text-white py-4 px-6 rounded-lg font-semibold hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap text-lg"
            >
              {isSubmitting ? 'Processing...' : 'Continue to Payment'}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
